import { z } from 'zod';
import { MAX_AGENT_CATEGORY_LENGTH } from '@/constants/agentCategories';

export const agentBuilderAgentTypeSchema = z.enum(['patient', 'customer', 'examiner', 'coach']);
export const agentBuilderInteractionModeSchema = z.enum(['voice', 'video']);

export const agentBuilderDocumentSectionSchema = z.object({
  heading: z.string().trim().min(1),
  content: z.string().trim().min(1),
});

export const agentBuilderOutputTemplateSchema = z.object({
  title: z.string().trim().min(1),
  description: z.string().trim().min(1),
  sections: z.array(agentBuilderDocumentSectionSchema).min(1).max(20),
});

export const agentBuilderDraftSchema = z.object({
  agentType: agentBuilderAgentTypeSchema,
  interactionMode: agentBuilderInteractionModeSchema,
  tileTitle: z.string().trim().min(1),
  personaName: z.string().trim().min(1),
  roleTitle: z.string().trim().min(1),
  roleplayFocus: z.string().trim().min(1),
  briefDescription: z.string().trim().min(1),
  category: z.string().trim().min(1).max(MAX_AGENT_CATEGORY_LENGTH),
  domain: z.string().trim().min(1),
  specialty: z.string().trim(),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']),
  estimatedDurationMinutes: z.number().int().min(5).max(120),
  openingMessage: z.string().trim(),
  sections: z.object({
    personality: z.string().trim().min(1),
    environment: z.string().trim().min(1),
    tone: z.string().trim().min(1),
    goal: z.string().trim().min(1),
    guardrails: z.string().trim().min(1),
  }),
  repPreparation: z.object({
    preparationContent: z.string().trim().min(1),
    simulationObjective: z.string().trim().min(1),
  }),
  supportingInformation: z.object({
    title: z.string().trim().min(1),
    purpose: z.string().trim().min(1),
    sections: z.array(agentBuilderDocumentSectionSchema).min(1).max(12),
  }),
  outputTemplate: agentBuilderOutputTemplateSchema.nullable(),
});

export const agentBuilderResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('question'),
    message: z.string().trim().min(1),
    quickReplies: z.array(z.string().trim().min(1)).max(6).optional(),
  }),
  z.object({
    kind: z.literal('draft'),
    message: z.string().trim().min(1),
    draft: agentBuilderDraftSchema,
  }),
]);

export const agentBuilderGenerationStatusSchema = z.enum([
  'interview',
  'generating',
  'ready',
  'failed',
]);

export const agentBuilderSavedMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  quickReplies: z.array(z.string()).optional(),
});

export const agentBuilderConversationSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  messages: z.array(agentBuilderSavedMessageSchema),
  // Drafts are persisted provider output. Validate them at the point where
  // they are applied so a malformed historical row can still be opened and
  // shown as failed instead of making the entire status response unparsable.
  draft: z.unknown().nullish(),
  generationStatus: agentBuilderGenerationStatusSchema,
  errorMessage: z.string().nullish(),
  errorReference: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const agentBuilderConversationSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  generation_status: agentBuilderGenerationStatusSchema,
  error_reference: z.string().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
  last_activity_at: z.string(),
});

export const agentBuilderApiResponseSchema = z.discriminatedUnion('kind', [
  agentBuilderResponseSchema.options[0].extend({ conversationId: z.string().uuid() }),
  z.object({
    kind: z.literal('queued'),
    conversationId: z.string().uuid(),
    generationStatus: z.literal('generating'),
    reference: z.string(),
    message: z.string(),
  }),
  z.object({
    kind: z.literal('conversation'),
    conversation: agentBuilderConversationSchema,
  }),
  z.object({
    kind: z.literal('status'),
    conversation: agentBuilderConversationSchema,
  }),
  z.object({
    kind: z.literal('conversations'),
    conversations: z.array(agentBuilderConversationSummarySchema),
  }),
  z.object({
    kind: z.literal('deleted'),
    conversationId: z.string().uuid(),
  }),
]);

export type AgentBuilderDraft = z.infer<typeof agentBuilderDraftSchema>;
export type AgentBuilderOutputTemplate = z.infer<typeof agentBuilderOutputTemplateSchema>;
export type AgentBuilderResponse = z.infer<typeof agentBuilderResponseSchema>;
export type AgentBuilderAgentType = z.infer<typeof agentBuilderAgentTypeSchema>;
export type AgentBuilderApiResponse = z.infer<typeof agentBuilderApiResponseSchema>;
export type AgentBuilderConversation = z.infer<typeof agentBuilderConversationSchema>;
export type AgentBuilderConversationSummary = z.infer<typeof agentBuilderConversationSummarySchema>;
export type AgentBuilderGenerationStatus = z.infer<typeof agentBuilderGenerationStatusSchema>;

export interface AgentBuilderChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
